import {
  AlertTriangle,
  GraduationCap,
  Mail,
  Pencil,
  Send,
  Trash2,
  Users,
  User,
} from "lucide-react";
import WelcomeCard from "../../components/WelcomeCard";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllCohorts } from "../../services/adminService";
import { useSelector } from "react-redux";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";
import { useState } from "react";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { api1 } from "../../utils/axiosInstance";
import Modal from "../../components/modal/Modal";
import { toast } from "react-toastify";

const ListGroupMentor = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEmailOpen, setModalEmailOpen] = useState(false);
  const [modalMenteesEmailOpen, setModalMenteesEmailOpen] = useState(false);
  const [usersDetailModal, setUsersDetailModal] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [mentorId, setMentorId] = useState(null);
  const [emailSubject, setEmailSubject] = useState();
  const [emailMessage, setEmailMessage] = useState();
  const [cohort, setCohort] = useState(null);
  const userId = useSelector((state) => state.auth.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const formattedDate = (date) => {
    const fdate = new Date(date);
    return fdate.toLocaleString();
  };

  const {
    data: fetchAllCohortsData,
    isLoading: fetchAllCohortsIsLoading,
    error: fetchAllCohortsError,
  } = useQuery({
    queryKey: ["totalCohorts"],
    queryFn: fetchAllCohorts,
    enabled: !!userId,
  });

  const getAllUsers = (cohortId = null) => {
    if (!fetchAllCohortsData || !Array.isArray(fetchAllCohortsData)) {
      return [];
    }

    let targetCohorts = fetchAllCohortsData;
    
    if (cohortId) {
      targetCohorts = fetchAllCohortsData.filter(cohort => cohort._id === cohortId);
    }

    const allUsers = targetCohorts
      .filter(
        (cohort) =>
          cohort.registeredUsers && Array.isArray(cohort.registeredUsers)
      )
      .flatMap((cohort) => cohort.registeredUsers);

    // Remove duplicates based on _id
    const uniqueUsers = allUsers.filter((user, index, self) => 
      index === self.findIndex((u) => u._id === user._id)
    );

    return uniqueUsers;
  };

  const handleModalClose = () => {
    setEmailMessage(null);
    setEmailSubject(null);
    setCohort(null);
    setModalOpen(false);
  };

  const handleMenteesModalClose = () => {
    setEmailMessage(null);
    setEmailSubject(null);
    setCohort(null);
    setModalMenteesEmailOpen(false);
  };

  const handleModal = (cohort) => {
    setCohort(cohort);
    setModalOpen(true);
  };

  const handleMenteesModal = (cohort) => {
    setCohort(cohort);
    setModalMenteesEmailOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api1.delete(`${API_ENDPOINTS.CHORTS}/${cohort._id}`);
      queryClient.invalidateQueries(["totalCohorts"]);
    } catch (error) {
      console.error("Error deleting chorts:", error);
    } finally {
      setModalOpen(false);
    }
  };

  const handleModalOpen = (cohort) => {
    setMentorId(cohort.mentor._id);
    setModalEmailOpen(true);
  };

  const handleEmailModalClose = () => {
    setModalEmailOpen(false);
  };

  const handleSendEmail = async () => {
    if (emailMessage && emailSubject) {
      try {
        await api1.post(API_ENDPOINTS.SEND_EMAIL_TO_MENTOR, {
          mentorId,
          subject: emailSubject,
          message: emailMessage,
        });
      } catch (error) {
        console.error(error);
      } finally {
        handleModalClose();
      }
    } else {
      toast.error("Please fill the email");
    }
  };

  const handleSendBulkEmail = async () => {
    if (emailMessage && emailSubject) {
      if (cohort.registeredUsers.length > 0) {
        try {
          await api1.post(API_ENDPOINTS.SEND_BULK_EMAILS, {
            ids: cohort.registeredUsers,
            subject: emailSubject,
            message: emailMessage,
          });
        } catch (error) {
          console.error(error);
        } finally {
          handleMenteesModalClose();
        }
      } else {
        toast.error("No Registered Users");
      }
    } else {
      toast.error("Please fill the email");
    }
  };

  const handleUsersDetailModalClose = () => {
    setUsersDetailModal(false);
    setSelectedCohort(null);
  };

  const handleUsersDetailModalOpen = (cohort) => {
    setSelectedCohort(cohort);
    setUsersDetailModal(true);
  };

  if (fetchAllCohortsIsLoading) {
    return <SecondaryLoader />;
  }

  if (fetchAllCohortsError) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-gray-50 rounded-lg shadow-inner border border-gray-200">
        <GraduationCap className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700">
          Error Fetching Cohorts
        </h3>
        <p className="text-sm text-gray-500 mt-2">
          There was an error fetching the Cohorts. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={usersDetailModal}
        onClose={handleUsersDetailModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Registered Users {selectedCohort ? `- ${selectedCohort.groupName}` : ''}
          </h2>
          {getAllUsers(selectedCohort?._id)?.length > 0 ? (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {getAllUsers(selectedCohort?._id).map((user) => {
                const profile = user.profile;
                const firstName = profile?.firstName || 'N/A';
                const lastName = profile?.lastName || 'N/A';
                const currentJob = profile?.currentJob || 'N/A';
                const city = profile?.city || 'N/A';
                const country = profile?.country || 'N/A';
                const commonSkills = profile?.commonSkills || [];
                const profilePhoto = profile?.profilePhoto;
                
                return (
                  <div
                    key={user._id}
                    className="flex items-start p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  >
                    {/* Profile Photo */}
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt={`${firstName} ${lastName}`}
                        className="w-16 h-16 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                        <User className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    {/* User Details */}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {firstName} {lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{currentJob}</p>
                      <p className="text-sm text-gray-500">
                        {city}, {country}
                      </p>
                      <div className="mt-2">
                        <span className="text-xs font-medium text-gray-700">
                          Skills:{" "}
                        </span>
                        <span className="text-xs text-gray-600">
                          {commonSkills.length > 0 ? commonSkills.join(", ") : "None"}
                        </span>
                      </div>
                      {/* More Details Button */}
                      <button
                        onClick={() =>
                          navigate(`/dashboard/users/${user._id}`)
                        }
                        className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        More Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500">No users found.</p>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={modalEmailOpen}
        onClose={handleEmailModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Send Email to Mentor
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                rows={6}
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Write your message here..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleEmailModalClose}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                className="px-4 py-2 rounded bg-[#7E5BEF] text-white hover:bg-[#674dd0]"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalMenteesEmailOpen}
        onClose={handleMenteesModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Send Email to Mentees
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                rows={6}
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-[#7E5BEF]"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Write your message here..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleMenteesModalClose}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBulkEmail}
                className="px-4 py-2 rounded bg-[#7E5BEF] text-white hover:bg-[#674dd0]"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalOpen}
        onClose={handleModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="flex flex-col items-center justify-center gap-4 p-4">
          <div className="text-red-600">
            <AlertTriangle className="w-12 h-12" />
          </div>

          <h2 className="text-lg md:text-xl font-semibold text-center text-gray-800">
            Are you sure you want to delete this cohort?
          </h2>
          <p className="text-sm text-gray-500 text-center">
            This action cannot be undone.
          </p>

          <div className="flex justify-center gap-4 mt-4 w-full">
            <button
              onClick={handleModalClose}
              className="px-4 py-2 w-full border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 w-full bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <div className="flex flex-col gap-6">
        <WelcomeCard title={"List Group Mentoring"} />
        {fetchAllCohortsData?.length > 0 ? (
          <div className="p-4 sm:p-6 bg-white min-h-screen rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Cohorts Lists
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="border-y bg-gray-100">
                  <tr className="uppercase text-xs sm:text-sm text-left text-gray-600">
                    <th className="py-3 px-4">Group Name</th>
                    <th className="py-3 px-4">Mentor Name</th>
                    <th className="py-3 px-4">Users</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {fetchAllCohortsData?.map((cohort, index) => (
                    <tr
                      key={index}
                      className="border-b hover:bg-gray-50 transition text-sm"
                    >
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-800">
                          {cohort.groupName}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-800">
                          {cohort.mentor.name}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {cohort.registeredUsers.length}
                      </td>
                      <td className="py-4 px-4 text-gray-600">
                        {cohort.description}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-wrap sm:flex-nowrap justify-center overflow-hidden rounded-md">
                          <button
                            title="Edit"
                            onClick={() =>
                              navigate(
                                `/dashboard/group-mentoring/create/${cohort._id}`,
                                {
                                  state: { cohort },
                                }
                              )
                            }
                            className="text-white text-xs sm:text-sm font-medium bg-green-600 hover:bg-green-700 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 transition-all duration-200 shadow-sm hover:shadow rounded-l-md"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>

                          <button
                            title="Delete"
                            onClick={() => handleModal(cohort)}
                            className="text-white text-xs sm:text-sm font-medium bg-rose-600 hover:bg-rose-700 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 transition-all duration-200 shadow-sm hover:shadow"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleModalOpen(cohort)}
                            title="Mentor Email"
                            className="text-white text-xs sm:text-sm hover:opacity-90 flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 px-3 sm:px-5 py-2"
                          >
                            <Send className="w-3" />
                          </button>

                          <button
                            onClick={() => handleMenteesModal(cohort)}
                            title="Mentees Email"
                            className="text-white text-xs sm:text-sm hover:opacity-90 flex items-center gap-1 bg-yellow-600 hover:bg-yellow-700 px-3 sm:px-5 py-2"
                          >
                            <Mail className="w-3" />
                          </button>

                          <button
                            title="View Mentees"
                            onClick={() => handleUsersDetailModalOpen(cohort)}
                            className="text-white text-xs sm:text-sm hover:opacity-90 flex items-center gap-1 bg-sky-600 hover:bg-sky-700 px-3 sm:px-5 py-2 rounded-r-md"
                          >
                            <Users className="w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-gray-50 rounded-lg shadow-inner border border-gray-200">
            <GraduationCap className="w-12 h-12 text-indigo-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">
              No Groups Found
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              You haven't created any groups yet.
            </p>
          </div>
        )}
      </div>
    </>
  );
};
export default ListGroupMentor;
